import { FC, useEffect, useMemo, useState } from 'react';
import { BTag, FlexBox, useBreakpoint } from '@brg/ui';
import {  API, Endpoints, Tag } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { bTag, LayoutConst, ProfileUtils, useModal } from 'core';
import { ModalButtonFilled, ModalButtonHollow, Subtitle, Title, Wrapper } from './styles';
import { Checkbox } from 'shared/pages/projectEdit/components/PresentatitonBlock/PresentatitonBlock';
import { useGetProjectTags } from 'shared/hooks/api/useProjectsByFilter';
import { useDesignerById } from 'shared/hooks/api/useDesignerById';
import { BTagsGrid } from 'core/components';
import { LoaderSpinner } from '../artzLegalsModal/LoaderAnimation';
import { useProfile } from '@brg/auth';

/**
 * @param Модалка подписки на юзера @Artem
 */

export const SubscribeUserModal: FC<{
    accountId: string,
    isResumeSubscribe?: boolean,
    onSubmit?: (subscriberId: string, targetId: string, ids: string[]) => void,
}> = ({
    accountId, 
    isResumeSubscribe,
    onSubmit, 
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const isMobile = useBreakpoint(743);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const [designer] = useDesignerById(accountId, i18n.language);

    const designerName = useMemo(
        () => (designer ? ProfileUtils.getFullNameString(designer) : '') 
    , [designer])

    const disableSubmit = useMemo(
        () => (!selectedTags?.length)  
    , [selectedTags]);

    const filter = useMemo(() => {
        if (accountId) {
            return {
                isMainAuthor: true,
                projectFilter: 1,
                authorIds: [accountId],
                isContextIgnored: true,
                limit: 1000,
            }
        }

        return null;
    } , [accountId])

    const [tags, tagLoading] = useGetProjectTags(filter, i18n.language)

    const isSelected = (id: string) => {
        return selectedTags?.includes(id)
    }

    const sortByName = (array: Tag<'Local'>[], language = 'ru') => {
        return [...array].sort((a, b) =>
          a.name.localeCompare(b.name, language, { sensitivity: 'base' })
        );
    }

    const sortedTags = useMemo(() => {
        if (!tags) return [];
        const selected = tags?.filter((tag) => {
            return isSelected(tag?.tagId)
        })

        const unselected = tags?.filter((tag) => {
            return !isSelected(tag?.tagId)
        })

        const educationTags = tags?.filter((tag) => {
            return !!tag?.isEducationTag
        })

        const simpleTags = tags?.filter((tag) => {
            return !tag?.isEducationTag
        })

        const byName = sortByName(unselected, i18n.language)

        const byNameEducation = sortByName(educationTags, i18n.language)
        const byNameSimbple= sortByName(simpleTags, i18n.language)

        return isResumeSubscribe ? [...selected, ...byName] : [...byNameEducation, ...byNameSimbple]
    } , [tags, i18n.language, isResumeSubscribe])

    const checked = useMemo( 
        () => (tags && tags?.length > 0 ? 
            (selectedTags?.length === tags?.length) 
        : false)
    , [selectedTags, tags]) 

        const fetchSubscribersTags = async () => {
            API.request()
            .endpoint(Endpoints.subscriptions)
            .url('/api/Subscription/GetSubscriptions')
            .addQuery('subscriberId', profile?.accountId ?? '')
            .addQuery('subscriberType' , '0')
            .addQuery('targetType', 'userProjects')
            .get()
            .send()
            .then((r) => r.json())
            .then(({ data}) => {
                if (data) {
                    const find = data?.find(
                        (subscribe: any) => {
                        return subscribe?.targetId === accountId && 
                        subscribe?.subscriberId === profile?.accountId
                    })
                 setSelectedTags([...(!!find ? find?.tagIds : [])])
                }
            })
        }
    
    useEffect(() => {
        fetchSubscribersTags();
    } , [])

    const onCancel = () => {
        modal.hide()
    }

    const onTagClick = (id: string) => {
        const find = selectedTags.filter((i) => i === id)
        setSelectedTags(!!find?.length ? 
            selectedTags.filter((i) => i !== id) 
            : [...selectedTags, id]
        )
    }

    const onClick = () => {

        if (!profile || !accountId) return;
        onSubmit && onSubmit(
            profile?.accountId, 
            accountId, 
            selectedTags
        )
        setTimeout(() => {
            window.location.reload();
        }, 1000)
    }

    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center'>
               <FlexBox direction='column' directionMobile='column' gy={16} gyMobile={is992 ? 8 : 16}>
                    <Title color={theme.not_saved_modal.title}>
                        {t('subscription.modal.title', { name: designerName })}
                    </Title>
                    <Subtitle color={'#151514'}>
                    {t('subscription.modal.subtitle')}
                    </Subtitle>
               </FlexBox>
               <FlexBox>
                    <Checkbox 
                        label={t('subscription.modal.choose_all')}
                        checked={checked}
                        dark 
                        onClick={() => {
                            if (!!checked) {
                                setSelectedTags([]);
                                return;
                            }
                            const tagsIds = sortedTags?.map((tag) => tag?.tagId)
                            setSelectedTags(tagsIds || [])
                        }}
                    />
                </FlexBox>
               <BTagsGrid style={{ maxHeight: 156, minHeight: 156}} gap={8} scroll={!tagLoading} scrollHeight={156}>
                {tagLoading && (
                    <FlexBox height={156}>
                        <LoaderSpinner hideText/>
                    </FlexBox>
                )}
                {!!sortedTags?.length && sortedTags?.map((tag: Tag) => {
                    return (
                        <BTag
                            tag={tag}
                            onClick={() => onTagClick && onTagClick(tag.tagId)}
                            key={tag.tagId}
                            selected={isSelected && isSelected(tag?.tagId)}
                            educational={tag?.isEducationTag}
                            //@ts-ignore
                            selectedColor={{
                                backgroundColor: '#70706F !important',
                                color: '#fefefe !important',
                                '&:hover': {
                                    backgroundColor: '#70706F !important',
                                    color: '#fefefe !important',
                                    borderColor: 'transparent !important'
                                }
                            }}
                            organization={false}
                            isProjectsTag
                        >
                        {bTag.title(tag)}
                        </BTag>)}
                    )
                }
                </BTagsGrid>
               <FlexBox 
                    width={'100%'} 
                    direction='row' 
                    directionMobile={isMobile ? 'column' : 'row'} 
                    gx={24} 
                    gxMobile={isMobile ? 1 : 24}
                    gy={0}
                    gyMobile={isMobile ? 8 : 1}
                >
                    <ModalButtonHollow 
                        onClick={onCancel}
                    >
                        {t('subscription.cancel')}
                    </ModalButtonHollow>
                    <ModalButtonFilled 
                        disabled={disableSubmit} 
                        onClick={onClick}
                        isResumeSubscribe={isResumeSubscribe}
                    >
                        {t(`subscription.${isResumeSubscribe ? 'save' : 'subscribe'}`)}
                    </ModalButtonFilled>
               </FlexBox>
        </Wrapper>

};
import { FC, useEffect, useMemo, useState } from 'react';
import { BTag, FlexBox, useBreakpoint } from '@brg/ui';
import {  API, Endpoints, Tag } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { bTag, LayoutConst, ProfileUtils, useModal } from 'core';
import { ModalButtonFilled, ModalButtonHollow, Subtitle, Title, Wrapper } from './styles';
import { Checkbox } from 'shared/pages/projectEdit/components/PresentatitonBlock/PresentatitonBlock';
import { useGetProjectTags } from 'shared/hooks/api/useProjectsByFilter';
import { useDesignerById } from 'shared/hooks/api/useDesignerById';
import { BTagsGrid } from 'core/components';
import { LoaderSpinner } from '../artzLegalsModal/LoaderAnimation';
import { useProfile } from '@brg/auth';

/**
 * @param Модалка подписки на юзера @Artem
 */

export const SubscribeUserModal: FC<{
    accountId: string,
    isResumeSubscribe?: boolean,
    onSubmit?: (subscriberId: string, targetId: string, ids: string[]) => void,
}> = ({
    accountId, 
    isResumeSubscribe,
    onSubmit, 
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const isMobile = useBreakpoint(743);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const [designer] = useDesignerById(accountId, i18n.language);

    const designerName = useMemo(
        () => (designer ? ProfileUtils.getFullNameString(designer) : '') 
    , [designer])

    const disableSubmit = useMemo(
        () => (!selectedTags?.length)  
    , [selectedTags]);

    const filter = useMemo(() => {
        if (accountId) {
            return {
                isMainAuthor: true,
                projectFilter: 1,
                authorIds: [accountId],
                isContextIgnored: true,
                limit: 1000,
            }
        }

        return null;
    } , [accountId])

    const [tags, tagLoading] = useGetProjectTags(filter, i18n.language)

    const isSelected = (id: string) => {
        return selectedTags?.includes(id)
    }

    const sortByName = (array: Tag<'Local'>[], language = 'ru') => {
        return [...array].sort((a, b) =>
          a.name.localeCompare(b.name, language, { sensitivity: 'base' })
        );
    }

    const sortedTags = useMemo(() => {
        if (!tags) return [];
        const selected = tags?.filter((tag) => {
            return isSelected(tag?.tagId)
        })

        const unselected = tags?.filter((tag) => {
            return !isSelected(tag?.tagId)
        })

        const educationTags = tags?.filter((tag) => {
            return !!tag?.isEducationTag
        })

        const simpleTags = tags?.filter((tag) => {
            return !tag?.isEducationTag
        })

        const byName = sortByName(unselected, i18n.language)

        const byNameEducation = sortByName(educationTags, i18n.language)
        const byNameSimbple= sortByName(simpleTags, i18n.language)

        return isResumeSubscribe ? [...selected, ...byName] : [...byNameEducation, ...byNameSimbple]
    } , [tags, i18n.language, isResumeSubscribe])

    const checked = useMemo( 
        () => (tags && tags?.length > 0 ? 
            (selectedTags?.length === tags?.length) 
        : false)
    , [selectedTags, tags]) 

        const fetchSubscribersTags = async () => {
            API.request()
            .endpoint(Endpoints.subscriptions)
            .url('/api/Subscription/GetSubscriptions')
            .addQuery('subscriberId', profile?.accountId ?? '')
            .addQuery('subscriberType' , '0')
            .addQuery('targetType', 'userProjects')
            .get()
            .send()
            .then((r) => r.json())
            .then(({ data}) => {
                if (data) {
                    const find = data?.find(
                        (subscribe: any) => {
                        return subscribe?.targetId === accountId && 
                        subscribe?.subscriberId === profile?.accountId
                    })
                 setSelectedTags([...(!!find ? find?.tagIds : [])])
                }
            })
        }
    
    useEffect(() => {
        fetchSubscribersTags();
    } , [])

    const onCancel = () => {
        modal.hide()
    }

    const onTagClick = (id: string) => {
        const find = selectedTags.filter((i) => i === id)
        setSelectedTags(!!find?.length ? 
            selectedTags.filter((i) => i !== id) 
            : [...selectedTags, id]
        )
    }

    const onClick = () => {

        if (!profile || !accountId) return;
        onSubmit && onSubmit(
            profile?.accountId, 
            accountId, 
            selectedTags
        )
        setTimeout(() => {
            window.location.reload();
        }, 1000)
    }

    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center'>
               <FlexBox direction='column' directionMobile='column' gy={16} gyMobile={is992 ? 8 : 16}>
                    <Title color={theme.not_saved_modal.title}>
                        {t('subscription.modal.title', { name: designerName })}
                    </Title>
                    <Subtitle color={'#151514'}>
                    {t('subscription.modal.subtitle')}
                    </Subtitle>
               </FlexBox>
               <FlexBox>
                    <Checkbox 
                        label={t('subscription.modal.choose_all')}
                        checked={checked}
                        dark 
                        onClick={() => {
                            if (!!checked) {
                                setSelectedTags([]);
                                return;
                            }
                            const tagsIds = sortedTags?.map((tag) => tag?.tagId)
                            setSelectedTags(tagsIds || [])
                        }}
                    />
                </FlexBox>
               <BTagsGrid style={{ maxHeight: 156, minHeight: 156}} gap={8} scroll={!tagLoading} scrollHeight={156}>
                {tagLoading && (
                    <FlexBox height={156}>
                        <LoaderSpinner hideText/>
                    </FlexBox>
                )}
                {!!sortedTags?.length && sortedTags?.map((tag: Tag) => {
                    return (
                        <BTag
                            tag={tag}
                            onClick={() => onTagClick && onTagClick(tag.tagId)}
                            key={tag.tagId}
                            selected={isSelected && isSelected(tag?.tagId)}
                            educational={tag?.isEducationTag}
                            //@ts-ignore
                            selectedColor={{
                                backgroundColor: '#70706F !important',
                                color: '#fefefe !important',
                                '&:hover': {
                                    backgroundColor: '#70706F !important',
                                    color: '#fefefe !important',
                                    borderColor: 'transparent !important'
                                }
                            }}
                            organization={false}
                            isProjectsTag
                        >
                        {bTag.title(tag)}
                        </BTag>)}
                    )
                }
                </BTagsGrid>
               <FlexBox 
                    width={'100%'} 
                    direction='row' 
                    directionMobile={isMobile ? 'column' : 'row'} 
                    gx={24} 
                    gxMobile={isMobile ? 1 : 24}
                    gy={0}
                    gyMobile={isMobile ? 8 : 1}
                >
                    <ModalButtonHollow 
                        onClick={onCancel}
                    >
                        {t('subscription.cancel')}
                    </ModalButtonHollow>
                    <ModalButtonFilled 
                        disabled={disableSubmit} 
                        onClick={onClick}
                        isResumeSubscribe={isResumeSubscribe}
                    >
                        {t(`subscription.${isResumeSubscribe ? 'save' : 'subscribe'}`)}
                    </ModalButtonFilled>
               </FlexBox>
        </Wrapper>

};
